import React, { useMemo, useState } from "react";
import { computeGroups } from "../lib/grouping";
import { rowArea } from "../lib/cellValue";
import { Layers, Combine } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { useI18n } from "../context/LanguageContext";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";

const LINE1_KEYS = ["qty", "blindW", "blindH", "width", "height"];

export default function SummaryPanel({ rows, rowColors, onMergeGroup, onRenumber, columns, blindOffsets }) {
    const { t } = useI18n();
    const [mergeTarget, setMergeTarget] = useState({});
    const [pending, setPending] = useState(null);
    const [renumberOpen, setRenumberOpen] = useState(false);
    const { groups } = useMemo(() => computeGroups(rows), [rows]);

    const cols = (columns || []).filter((c) => !c.hidden);
    const totalQty = groups.reduce((a, g) => a + g.rows.reduce((x, r) => x + (Number(r.qty) || 0), 0), 0);

    const colLabel = (c) => (c.calc === "blindW" || c.calc === "blindH" ? t(c.calc === "blindW" ? "ot_blind_w" : "ot_blind_h") : c.label);
    const colValue = (c, members) => {
        if (c.key === "qty") return members.reduce((a, r) => a + (Number(r.qty) || 0), 0);
        if (c.calc === "area") {
            const a = members.reduce((s, r) => s + (rowArea(r) || 0), 0);
            return a ? Math.round(a * 1000) / 1000 : "—";
        }
        if (c.calc === "blindW" || c.calc === "blindH") {
            const dimKey = c.calc === "blindW" ? "width" : "height";
            const offKey = c.calc === "blindW" ? "blindOffW" : "blindOffH";
            const globalOff = c.calc === "blindW" ? (blindOffsets?.width ?? 0) : (blindOffsets?.height ?? 0);
            const set = [...new Set(members.map((r) => {
                const d = Number(r[dimKey]);
                if (!Number.isFinite(d) || d <= 0) return null;
                return d + Number(r[offKey] ?? globalOff ?? 0);
            }).filter((v) => v != null))];
            return set.length ? set.join(", ") : "—";
        }
        const vals = [...new Set(members.map((r) => String(r[c.key] ?? "").trim()).filter(Boolean))];
        return vals.length ? vals.join(", ") : "—";
    };
    const qtyCol = cols.find((c) => c.key === "qty");
    const dimCols = ["blindW", "blindH"].map((k) => cols.find((c) => c.calc === k)).filter(Boolean);
    const dims = dimCols.length === 2 ? dimCols : ["width", "height"].map((k) => cols.find((c) => c.key === k) || { key: k });
    const posCol = cols.find((c) => c.key === "blindPos");
    const infoCols = cols.filter((c) => !LINE1_KEYS.includes(c.key) && !c.calc && c.key !== "blindPos" && c.key !== "position");
    const GRID_LAYOUT = ["blindW", "blindH", null, "motor", ["type", "side"], ["color", "kabel type"], "width", "height", "position",
        ["notes", "infesting"], ["borring", "borring styr"], ["leng_al", "leng. al skims"]];
    const gridCols = (() => {
        const norm = (s) => String(s || "").trim().toLowerCase();
        const find = (k) => {
            const [key, label] = Array.isArray(k) ? k : [k, null];
            return cols.find((c) => norm(c.label) === label && label) || cols.find((c) => (c.calc ? c.calc === key : c.key === key));
        };
        const used = new Set();
        const laid = GRID_LAYOUT.map((k, i) => {
            const c = k && find(k);
            if (c && !used.has(c)) { used.add(c); return c; }
            return { key: `__pad${i}`, spacer: true };
        });
        const rest = cols.filter((c) => !used.has(c) && c.key !== "qty" && c.key !== "blindPos");
        return [...laid, ...rest];
    })();
    const posInfo = (members, groupQty) => {
        const line1 = `${qtyCol?.label || "Ant."} ${groupQty} | ${dims.map((c) => colValue(c, members)).join(" | ")}`;
        const rest = infoCols.map((c) => ({ key: c.key, label: colLabel(c), value: colValue(c, members) })).filter((x) => x.value !== "—");
        return { line1, rest };
    };

    return (
        <TooltipProvider delayDuration={250}>
        <div className="h-full overflow-auto p-4" data-testid="summary-panel">
            <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                    { label: t("sum_groups"), value: groups.length },
                    { label: t("sum_total"), value: totalQty },
                ].map((s) => (
                    <div key={s.label} className="bg-[#182232] border border-[#1e293b] rounded-lg p-3">
                        <div className="text-2xl font-mono font-bold text-slate-100">{s.value}</div>
                        <div className="text-xs text-slate-400 uppercase tracking-wider font-mono mt-0.5">{s.label}</div>
                    </div>
                ))}
            </div>

            {groups.length === 0 ? (
                <div className="text-center text-slate-500 py-10 text-sm">
                    {t("sum_empty")}
                </div>
            ) : (
                <div className="space-y-3">
                    {groups.map((g) => {
                        const members = g.rows;
                        const groupQty = members.reduce((a, r) => a + (Number(r.qty) || 0), 0);
                        const isDuplicate = members.length > 1;
                        const canMerge = members.length > 1;
                        const target = mergeTarget[g.key] || (members[0] && members[0].id);
                        const info = posInfo(members, groupQty);
                        return (
                            <div key={g.key} data-testid={`summary-group-${g.key}`}
                                className="bg-[#182232] border border-[#1e293b] rounded-lg p-4 hover:border-blue-500/40 transition-colors">
                                <div className="flex items-start justify-between gap-3">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div data-testid={`summary-pos-info-${g.key}`} className="min-w-0 flex-1 cursor-default">
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <span className="w-4 h-4 shrink-0 rounded-full border border-white/30" style={{ backgroundColor: rowColors?.[members[0]?.id]?.hex || g.color?.hex }} />
                                                    <span data-testid={`summary-pos-${g.key}`} className="font-mono font-semibold text-slate-100 break-words min-w-0">
                                                        {posCol ? colValue(posCol, members) : "—"}
                                                    </span>
                                                </div>
                                                <div className="min-w-0 mt-1.5">
                                                    <div data-testid={`summary-line1-${g.key}`} className="font-mono font-semibold text-sm text-slate-100 break-words">{info.line1}</div>
                                                    {info.rest.length > 0 && (
                                                        <div data-testid={`summary-line2-${g.key}`} className="mt-1 flex flex-wrap gap-x-2.5 gap-y-0.5 text-xs font-mono text-slate-300">
                                                            {info.rest.map((x) => (
                                                                <span key={x.key} className="min-w-0 break-words"><span className="text-slate-500">{x.label}</span> {x.value}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent data-testid={`summary-tooltip-${g.key}`} side="bottom" align="start"
                                            className="max-w-xs bg-[#0b0f17] border border-[#1e293b] text-slate-100 font-mono text-xs">
                                            <div className="font-semibold mb-1">{info.line1}</div>
                                            {cols.filter((c) => c.key !== "qty").map((c) => (
                                                <div key={c.key} className="break-words"><span className="text-slate-500">{colLabel(c)}:</span> {colValue(c, members)}</div>
                                            ))}
                                        </TooltipContent>
                                    </Tooltip>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {isDuplicate && (
                                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30 flex items-center gap-1">
                                                <Layers className="w-3 h-3" /> {t("sum_identical", { n: members.length })}
                                            </span>
                                        )}
                                        <span className="font-mono font-bold text-lg text-slate-100">{groupQty} <span className="text-xs text-slate-400">vnt.</span></span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-x-2 gap-y-2.5 mt-3 text-xs font-mono">
                                    {gridCols.map((c) => c.spacer ? <div key={c.key} /> : (
                                        <div key={c.key} data-testid={`summary-${g.key}-${c.key}`}>
                                            <div className="text-slate-500 truncate">{colLabel(c)}</div>
                                            <div className="text-slate-200 break-words">{colValue(c, members)}</div>
                                        </div>
                                    ))}
                                </div>
                                {canMerge && (
                                    <div className="mt-3 flex items-center gap-2 border-t border-[#1e293b] pt-3 flex-wrap">
                                        <span className="text-xs text-slate-400 font-mono">{t("sum_merge_to")}</span>
                                        <Select value={target} onValueChange={(v) => setMergeTarget((m) => ({ ...m, [g.key]: v }))}>
                                            <SelectTrigger data-testid={`merge-target-${g.key}`} className="h-7 w-20 bg-[#0b0f17] border-[#1e293b] text-slate-200 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                                                {members.map((r) => <SelectItem key={r.id} value={r.id}>{r.blindPos || r.position || "—"}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <Button data-testid={`merge-btn-${g.key}`} size="sm"
                                            className="h-7 bg-blue-600 hover:bg-blue-500 text-white text-xs"
                                            onClick={() => {
                                                const primary = members.find((r) => r.id === target) || members[0];
                                                const others = members.filter((r) => r.id !== primary.id);
                                                setPending({
                                                    target: primary.id,
                                                    memberIds: members.map((r) => r.id),
                                                    primaryLabel: primary.blindPos || primary.position || "—",
                                                    othersLabel: others.map((r) => r.blindPos || r.position || "—").join(", "),
                                                });
                                            }}>
                                            <Combine className="w-3.5 h-3.5 mr-1" /> {t("sum_merge_btn")}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
                <AlertDialogContent className="bg-[#111827] border-[#1e293b] text-slate-100" data-testid="merge-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("sum_merge_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            {pending && t("sum_merge_confirm_desc", { a: pending.othersLabel, b: pending.primaryLabel })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="merge-confirm-cancel" className="bg-transparent border-[#1e293b] text-slate-200 hover:bg-[#182232] hover:text-white">{t("sum_merge_cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid="merge-confirm-ok" className="bg-blue-600 hover:bg-blue-500 text-white"
                            onClick={() => {
                                if (pending && onMergeGroup) onMergeGroup(pending.target, pending.memberIds);
                                setPending(null);
                                setRenumberOpen(true);
                            }}>
                            {t("sum_merge_continue")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={renumberOpen} onOpenChange={setRenumberOpen}>
                <AlertDialogContent className="bg-[#111827] border-[#1e293b] text-slate-100" data-testid="renumber-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("sum_renumber_title")}</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">{t("sum_renumber_desc")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="renumber-no" className="bg-transparent border-[#1e293b] text-slate-200 hover:bg-[#182232] hover:text-white">{t("sum_renumber_no")}</AlertDialogCancel>
                        <AlertDialogAction data-testid="renumber-yes" className="bg-blue-600 hover:bg-blue-500 text-white"
                            onClick={() => { if (onRenumber) onRenumber(); setRenumberOpen(false); }}>
                            {t("sum_renumber_yes")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
        </TooltipProvider>
    );
}
